import { apiGet, apiPost, apiPut, apiDelete } from '../api';
import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiArrowLeft, FiCheck, FiX } from 'react-icons/fi';

// Helper function to format dates in local timezone
const formatLocalDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString(undefined, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit'
  });
};

function EmployeeManagement({ token, role, onDone }) {
  const [activeTab, setActiveTab] = useState('list');
  const [employees, setEmployees] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState({
    name: '',
    role: 'EMPLOYEE',
    passcode: '',
    manager_pin: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  
  // Device management state (Owner only)
  const [devices, setDevices] = useState([]);
  const [deviceFilter, setDeviceFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'list' || activeTab === 'add') {
        await fetchEmployees();
      }
      if (activeTab === 'dashboard') {
        await fetchStats();
      }
      if (activeTab === 'profiles') {
        await fetchProfiles();
      }
      if (activeTab === 'devices' && role === 'OWNER') {
        await fetchDevices();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchEmployees = async () => {
    const response = await apiGet('/api/employee/employees', token, 'employee-token');
    const data = await response.json();
    setEmployees(data);
  };

  const fetchStats = async () => {
    const response = await apiGet('/api/employee/dashboard/stats', token, 'employee-token');
    const data = await response.json();
    setStats(data);
  };

  const fetchProfiles = async () => {
    const response = await apiGet('/api/employee/profiles', token, 'employee-token');
    const data = await response.json();
    setProfiles(data);
  };

  const fetchAttendanceHistory = async (employeeId) => {
    const response = await apiGet(`/api/employee/employees/${employeeId}/attendance`, token, 'employee-token');
    const data = await response.json();
    setAttendanceHistory(data);
  };

  // Device management functions (Owner only)
  const fetchDevices = async () => {
    try {
      const statusParam = deviceFilter !== 'all' ? `?status=${deviceFilter}` : '';
      const response = await apiGet(`/api/devices/${statusParam}`, token);
      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      } else {
        console.error('Error fetching devices');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  const handleApproveDevice = async (deviceId) => {
    try {
      const response = await apiPost(`/api/devices/${deviceId}/approve/`, {}, token);
      if (response.ok) {
        window.showToast('Device approved successfully', 'success');
        fetchDevices();
      } else {
        const error = await response.json();
        window.showToast(error.error || 'Error approving device', 'error');
      }
    } catch (error) {
      console.error('Error approving device:', error);
      window.showToast('Error approving device', 'error');
    }
  };

  const handleRevokeDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to revoke this device? Users will no longer be able to use it.')) return;
    
    try {
      const response = await apiPost(`/api/devices/${deviceId}/revoke/`, {}, token);
      if (response.ok) {
        window.showToast('Device revoked successfully', 'success');
        fetchDevices();
      } else {
        const error = await response.json();
        window.showToast(error.error || 'Error revoking device', 'error');
      }
    } catch (error) {
      console.error('Error revoking device:', error);
      window.showToast('Error revoking device', 'error');
    }
  };

  const handleAddEmployee = () => {
    setModalMode('add');
    setCurrentEmployee({
      name: '',
      role: 'EMPLOYEE',
      passcode: '',
      manager_pin: ''
    });
    setShowModal(true);
  };

  const handleEditEmployee = (employee) => {
    setModalMode('edit');
    setCurrentEmployee({
      id: employee.id,
      name: employee.name,
      role: employee.role,
      passcode: '',
      manager_pin: ''
    });
    setShowModal(true);
  };

  const handleSubmitEmployee = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate manager PIN before setting submitting state
    if (currentEmployee.role === 'MANAGER' && modalMode === 'add' && !currentEmployee.manager_pin) {
      window.showToast('Manager PIN is required for managers', 'error');
      return;
    }
    if (currentEmployee.role === 'MANAGER' && currentEmployee.passcode && currentEmployee.manager_pin &&
        currentEmployee.passcode === currentEmployee.manager_pin) {
      window.showToast('Manager PIN must be different from the login passcode', 'error');
      return;
    }
    setIsSubmitting(true);
    
    // Prepare data to send
    const dataToSend = { ...currentEmployee };
    // Remove empty fields for edit mode
    if (modalMode === 'edit') {
      if (!dataToSend.passcode) delete dataToSend.passcode;
      if (!dataToSend.manager_pin) delete dataToSend.manager_pin;
    }
    // Remove manager_pin if not a manager
    if (dataToSend.role !== 'MANAGER') {
      delete dataToSend.manager_pin;
    }
    
    try {
      const response = modalMode === 'add'
        ? await apiPost('/api/employee/employees', dataToSend, token, 'employee-token')
        : await apiPut(`/api/employee/employees/${currentEmployee.id}`, dataToSend, token, 'employee-token');
      
      if (response.ok) {
        window.showToast(`Employee ${modalMode === 'add' ? 'added' : 'updated'} successfully`, 'success');
        setShowModal(false);
        fetchEmployees();
      } else {
        const error = await response.json();
        window.showToast(`Error: ${error.error || JSON.stringify(error)}`, 'error');
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      window.showToast('Error saving employee', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      const response = await apiDelete(`/api/employee/employees/${employeeId}`, token, 'employee-token');
      
      if (response.ok) {
        window.showToast('Employee deleted successfully', 'success');
        fetchEmployees();
      } else {
        const error = await response.json();
        window.showToast(`Error: ${error.error || 'Cannot delete employee'}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      window.showToast('Error deleting employee', 'error');
    }
  };

  const viewAttendance = async (employee) => {
    setSelectedEmployee(employee);
    await fetchAttendanceHistory(employee.id);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded bg-blue-700">
                <FiUsers size={22} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Employee Management</h1>
                <p className="text-slate-600 text-sm">Manage employees, profiles, and attendance</p>
              </div>
            </div>
            <button
              onClick={onDone}
              className="flex items-center px-6 py-3 bg-white text-slate-700 rounded border border-slate-300 hover:bg-slate-50 transition-all font-medium shadow-sm"
            >
              <FiArrowLeft size={16} className="mr-1.5" /> Back
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-4 font-semibold transition-all text-sm ${
                activeTab === 'dashboard' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-6 py-4 font-semibold transition-all text-sm ${
                activeTab === 'list' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Employees
            </button>
            <button
              onClick={() => setActiveTab('profiles')}
              className={`px-6 py-4 font-semibold transition-all text-sm ${
                activeTab === 'profiles' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Profiles
            </button>
            {role === 'OWNER' && (
              <button
                onClick={() => setActiveTab('devices')}
                className={`px-6 py-4 font-semibold transition-all text-sm ${
                  activeTab === 'devices' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Devices
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto p-6">

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <h3 className="text-slate-600 text-sm font-medium mb-2">Total Employees</h3>
              <p className="text-3xl font-semibold text-slate-900">{stats.total_employees}</p>
            </div>
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <h3 className="text-slate-600 text-sm font-medium mb-2">Active Employees</h3>
              <p className="text-3xl font-semibold text-emerald-700">{stats.active_employees}</p>
            </div>
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <h3 className="text-slate-600 text-sm font-medium mb-2">Managers</h3>
              <p className="text-3xl font-semibold text-blue-700">{stats.managers}</p>
            </div>
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <h3 className="text-slate-600 text-sm font-medium mb-2">Today's Attendance</h3>
              <p className="text-3xl font-semibold text-slate-900">{stats.today_attendance}</p>
            </div>
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <h3 className="text-slate-600 text-sm font-medium mb-2">Currently Clocked In</h3>
              <p className="text-3xl font-semibold text-blue-700">{stats.currently_clocked_in}</p>
            </div>
          </div>
        )}

        {/* Employees List Tab */}
        {activeTab === 'list' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Employees List</h2>
              <button
                onClick={handleAddEmployee}
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded border border-blue-700 shadow-sm"
              >
                Add Employee
              </button>
            </div>

            <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Name</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Role</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Manager PIN</th>
                    <th className="px-4 py-3 text-center text-slate-700 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr key={employee.id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-900 font-medium text-sm">{employee.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${
                          employee.role === 'OWNER' 
                            ? 'bg-purple-100 text-purple-700 border-purple-200' 
                            : employee.role === 'MANAGER' 
                              ? 'bg-blue-100 text-blue-700 border-blue-200' 
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {employee.has_manager_pin
                          ? <FiCheck size={15} className="text-emerald-600" />
                          : <FiX size={15} className="text-red-400" />}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => viewAttendance(employee)}
                          className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-1 rounded border border-slate-700 mr-2 text-sm"
                        >
                          Attendance
                        </button>
                        <button
                          onClick={() => handleEditEmployee(employee)}
                          className="bg-white hover:bg-slate-50 text-slate-700 px-3 py-1 rounded border border-slate-300 mr-2 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(employee.id)}
                          className="bg-red-700 hover:bg-red-800 text-white px-3 py-1 rounded border border-red-700 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Profiles Tab */}
        {activeTab === 'profiles' && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Employee Profiles</h2>
            <div className="bg-white rounded border border-slate-200 p-6 shadow-sm">
              <p className="text-slate-600 text-sm">
                {profiles.length === 0 
                  ? 'No employee profiles found. Profiles can be created from Django admin.'
                  : `${profiles.length} profile(s) found.`}
              </p>
            </div>
          </div>
        )}

        {/* Devices Tab (Owner Only) */}
        {activeTab === 'devices' && role === 'OWNER' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Device Management</h2>
              <div className="flex items-center gap-3">
                <select
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                  className="px-4 py-2 rounded border border-slate-300 bg-white text-slate-700 text-sm"
                >
                  <option value="all">All Devices</option>
                  <option value="pending">Pending Approval</option>
                  <option value="active">Active</option>
                </select>
                <button
                  onClick={fetchDevices}
                  className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded border border-slate-700 text-sm"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Device ID</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Status</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Employee</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Created</th>
                    <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Last Seen</th>
                    <th className="px-4 py-3 text-center text-slate-700 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices
                    .filter(device => {
                      if (deviceFilter === 'pending') return !device.is_active;
                      if (deviceFilter === 'active') return device.is_active;
                      return true;
                    })
                    .map((device) => (
                    <tr key={device.device_id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-900 font-mono text-xs">
                        {device.device_id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${
                          device.is_active 
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>
                          {device.is_active ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-sm">
                        {device.employee_name || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {new Date(device.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {device.last_seen 
                          ? new Date(device.last_seen).toLocaleString() 
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!device.is_active ? (
                          <button
                            onClick={() => handleApproveDevice(device.device_id)}
                            className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1 rounded border border-emerald-700 mr-2 text-sm"
                          >
                            Approve
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRevokeDevice(device.device_id)}
                            className="bg-red-700 hover:bg-red-800 text-white px-3 py-1 rounded border border-red-700 text-sm"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {devices.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-slate-500 text-sm">
                        No devices found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-4">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> Only devices registered by OWNER login attempts appear here. 
                Non-owner users attempting to login from unknown devices are silently rejected.
              </p>
            </div>
          </div>
        )}

        {/* Employee Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded border border-slate-200 p-6 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                {modalMode === 'add' ? 'Add Employee' : 'Edit Employee'}
              </h3>
              <form onSubmit={handleSubmitEmployee}>
                <div className="mb-4">
                  <label className="block text-slate-700 font-medium text-sm mb-2">Name</label>
                  <input
                    type="text"
                    value={currentEmployee.name}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, name: e.target.value})}
                    className="w-full px-4 py-2 rounded bg-white text-slate-900 border border-slate-300 focus:outline-none focus:border-blue-700 text-sm"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-slate-700 font-medium text-sm mb-2">Role</label>
                  <select
                    value={currentEmployee.role}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, role: e.target.value})}
                    className="w-full px-4 py-2 rounded bg-white text-slate-900 border border-slate-300 focus:outline-none focus:border-blue-700 text-sm"
                  >
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-slate-700 font-medium text-sm mb-2">Passcode</label>
                  <input
                    type="password"
                    value={currentEmployee.passcode}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, passcode: e.target.value})}
                    className="w-full px-4 py-2 rounded bg-white text-slate-900 border border-slate-300 focus:outline-none focus:border-blue-700 text-sm"
                    placeholder={modalMode === 'edit' ? 'Leave blank to keep current' : 'Enter passcode'}
                    required={modalMode === 'add'}
                  />
                </div>
                {currentEmployee.role === 'MANAGER' && (
                  <div className="mb-4">
                    <label className="block text-slate-700 font-medium text-sm mb-2">
                      Manager PIN {modalMode === 'add' && <span className="text-red-700">*</span>}
                    </label>
                    <input
                      type="password"
                      value={currentEmployee.manager_pin}
                      onChange={(e) => setCurrentEmployee({...currentEmployee, manager_pin: e.target.value})}
                      className="w-full px-4 py-2 rounded bg-white text-slate-900 border border-slate-300 focus:outline-none focus:border-blue-700 text-sm"
                      placeholder={modalMode === 'edit' ? 'Leave blank to keep current' : 'Enter 4-6 digit PIN'}
                      minLength="4"
                      maxLength="6"
                      required={modalMode === 'add' && currentEmployee.role === 'MANAGER'}
                    />
                    <p className="text-slate-500 text-xs mt-1">
                      {modalMode === 'add' ? 'Required for managers (4-6 digits)' : 'Leave blank to keep current PIN'}
                    </p>
                  </div>
                )}
                <div className="flex gap-4 pt-4 border-t border-slate-200">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-700 hover:bg-blue-800 text-white py-2 rounded border border-blue-700"
                  >
                    {modalMode === 'add' ? 'Add' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-white hover:bg-slate-50 text-slate-700 py-2 rounded border border-slate-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Attendance History Modal */}
        {selectedEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded border border-slate-200 p-6 w-full max-w-4xl max-h-screen overflow-y-auto shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-4 border-b border-slate-200">
                Attendance History - {selectedEmployee.name}
              </h3>
              <div className="bg-white rounded border border-slate-200 overflow-hidden mb-4">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Clock In</th>
                      <th className="px-4 py-3 text-left text-slate-700 font-semibold text-sm">Clock Out</th>
                      <th className="px-4 py-3 text-right text-slate-700 font-semibold text-sm">Duration (hrs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceHistory.map((record) => (
                      <tr key={record.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-900 text-sm">
                          {formatLocalDateTime(record.clock_in)}
                        </td>
                        <td className="px-4 py-3 text-slate-900 text-sm">
                          {record.clock_out ? formatLocalDateTime(record.clock_out) : (
                            <span className="text-blue-700 font-medium">Still clocked in</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-900 font-medium text-sm">
                          {record.duration ? record.duration.toFixed(2) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="w-full bg-white hover:bg-slate-50 text-slate-700 py-2 rounded border border-slate-300"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployeeManagement;
